import { CellContext } from '@tanstack/react-table';
import { TestQuestionRow } from './StepQuestionsResultsTable';
import styled from 'styled-components';
import { useAsync } from 'react-use';
import { SmallPortalTooltip } from '../../../Tooltip';
import { TooltipContent, TooltipTitle } from '../../Progress/Tooltip';
import { TooltipMarkdownContent } from '../common';
import { remark } from 'remark';
import stripMarkdown from 'strip-markdown';
import React from 'react';

export const WrappedText = styled.div`
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: fill-available;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textRegular};
  line-height: 1.5;
`;

export default function QuestionTextCell(
  props: CellContext<TestQuestionRow, unknown>
) {
  const maxHeight = 600;
  const text = props.row.original.section.content;
  const type = props.row.original.section.type;
  const stepId = props.row.original.stepId;
  const hasFollowUp = !!props.row.original.section?.followUpContent;
  let content = '';

  if (type === 'text-input') {
    content = text;
  }

  if (type === 'ranking') {
    const options = props.row.original.section.options ?? [];
    content = `${text}\n\n${options
      .map((option, index) => ` ${index + 1}. ${option.content}`)
      .join('\n')}`;
  }

  if (type === 'multiple-choice') {
    const options = props.row.original.section.options ?? [];
    content = `${text}\n\n${options
      .map((option, index) => ` ${index + 1}. ${option.content}`)
      .join('\n')}`;
  }

  if (type === 'multiple-rating') {
    const questions = props.row.original.section.questions ?? [];
    content = `${text}\n\n${questions
      .map(
        (question, index) =>
          `${index + 1}. ${question.content}\n\n > ${(question.options ?? [])
            .map((option) => `${option}`)
            .join(' / ')}`
      )
      .join('\n\n')}`;
  }

  if (type === 'matching') {
    const leftItems = props.row.original.section.leftItems ?? [];
    const rightItems = props.row.original.section.rightItems ?? [];
    content = `${text}\n\n# Left items\n\n${leftItems
      .map((item, index) => ` ${index + 1}. ${item.content}`)
      .join('\n')}\n\n# Right items\n\n${rightItems
      .map((item, index) => ` ${index + 1}. ${item.content}`)
      .join('\n')}`;
  }

  if (type === 'fill-in-the-gap' || type === 'text-template') {
    const template = props.row.original.section.template ?? '';
    content = `${text}\n\n# Template\n\n${template}\n\n`;
  }

  if (type === 'matrix-ranking') {
    const items = props.row.original.section.items ?? [];
    const horizontalAxisLabel = props.row.original.section.horizontalAxisLabel ?? '';
    const verticalAxisLabel = props.row.original.section.verticalAxisLabel ?? '';
    content = `${text}\n\n**X-axis:** ${horizontalAxisLabel}\n**Y-axis:** ${verticalAxisLabel}\n\n# Items\n\n${items
      .map((item, index) => ` ${index + 1}. ${item.content}`)
      .join('\n')}`;
  }

  if (type === 'py-playground') {
    content = `${text}\n\n${props.row.original.section.setupCode}\n\n${props.row.original.section.startingCode}`;
  }

  if (type === 'llm-playground') {
    content = `${text}\n\n${props.row.original.section.initialPrompt}`;
  }

  if (type === 'py-editor') {
    content = `${text}\n\n${props.row.original.section.startingCode}`;
  }

  if (type === 'llm-editor') {
    content = `${text}\n\n${props.row.original.section.initialPrompt}`;
  }
  const [isOverflowing, setIsOverflowing] = React.useState(false);
  const cleanMarkdown = useAsync(() => getCleanMarkdown(content), [content]);
  const onRefChange = React.useCallback(
    (ref: HTMLDivElement | null) => {
      if (ref) {
        if (ref.scrollHeight > maxHeight) {
          setIsOverflowing(true);
        } else {
          setIsOverflowing(false);
        }
      }
    },
    [maxHeight]
  );

  const uniqueId = `scenario-${stepId}-table-question-${props.row.original.section.id}`;
  return (
    <>
      <WrappedText data-tooltip-id={uniqueId}>
        {cleanMarkdown?.value || ''}
      </WrappedText>
      <SmallPortalTooltip anchorSelect={`[data-tooltip-id="${uniqueId}"]`}>
        <TooltipContent>
          <MainTextTruncateWrapper
            ref={onRefChange}
            $isOverflowing={isOverflowing}
            maxHeight={maxHeight}
          >
            <TooltipTitle>Question</TooltipTitle>
            <TooltipMarkdownContent md={`${content}`} />
          </MainTextTruncateWrapper>
          {hasFollowUp && (
            <>
              <TooltipTitle style={{ marginTop: 16 }}>
                Follow-up Question
              </TooltipTitle>
              <TooltipMarkdownContent
                md={`${props.row.original.section?.followUpContent ?? '*-*'}`}
              />
            </>
          )}
        </TooltipContent>
      </SmallPortalTooltip>
    </>
  );
}

export const MainTextTruncateWrapper = styled.div<{
  $isOverflowing: boolean;
  maxHeight: number;
}>`
  max-height: ${({ $isOverflowing, maxHeight }) =>
    $isOverflowing ? `${maxHeight}px` : 'none'};
  overflow: hidden;
  position: relative;

  /* Elegant fade-out gradient at the bottom - only show when overflowing */
  &::after {
    content: ${({ $isOverflowing }) => ($isOverflowing ? "''" : 'none')};
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 80px;
    background: linear-gradient(
      to bottom,
      transparent 0%,
      ${({ theme }) => theme.colors.bgPage} 100%
    );
    pointer-events: none;
    z-index: 1;
  }

  &::before {
    content: ${({ $isOverflowing }) => ($isOverflowing ? "'⋯'" : 'none')};
    position: absolute;
    bottom: 0;
    right: 50%;
    color: ${({ theme }) => theme.colors.textRegular};
    font-size: 16px;
    font-weight: bold;
    z-index: 2;
    pointer-events: none;
  }
`;

export const getCleanMarkdown = async (md: string) => {
  const processed = await remark().use(stripMarkdown).process(md);
  return processed.toString();
};
